import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  User,
  Trophy, 
  Calendar, 
  MapPin, 
  Award,
  TrendingUp,
  Star,
  Hash,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  BarChart3,
  Target,
  Zap,
  Medal,
  Activity,
  Clock,
  Shield,
  Shirt,
  Ruler
} from 'lucide-react';
import { playersApi } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';
import { EmptyStates } from '../Common';

const PlayerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // This will actually be the slug now
  const siteConfig = useSiteConfig();
  const [expandedSection, setExpandedSection] = useState<string | null>('stats');

  // Fetch player details (using slug)
  const { data: player, isLoading, error } = useQuery({
    queryKey: ['player', id],
    queryFn: async () => {
      if (!id) throw new Error('Player slug is required');
      return await playersApi.getBySlug(id); // Using getBySlug instead of getById
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-400">Loading player details...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Player Not Found</h3>
        <p className="text-gray-400 mb-6">We couldn't find details for this player.</p>
        <Breadcrumb
          items={[{ label: 'All Players', path: '/players' }]}
          showBackButton={true}
          backButtonText="Back to Players"
          backButtonPath="/players"
        />
      </div>
    );
  }

  // Extract player info from API response
  const playerName = `${player.person?.first_name || ''} ${player.person?.last_name || ''}`.trim();
  const position = player.primary_position_name || 'Player';
  const jerseyNumber = player.jersey_number || 'N/A';
  const age = player.person?.age || 'N/A';
  const birthDate = player.person?.birth_date || 'N/A';
  const draftInfo = player.draft_year ? 
    `Round ${player.draft_round}, Pick ${player.draft_pick} (${player.draft_year})` : 
    'Undrafted';

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          items={[
            { label: 'All Players', path: '/players' },
            { label: playerName || 'Player Details', isActive: true }
          ]}
        />

        {/* Player Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Player Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                <span className="text-4xl font-bold text-primary">
                  {jerseyNumber}
                </span>
              </div>
              {player.is_active && (
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Activity className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Player Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl font-bold text-white mb-2">{playerName}</h1>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <Shirt className="w-4 h-4" />
                  #{jerseyNumber}
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  {position}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Age: {age}
                </span>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {player.draft_year || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Draft Year</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">
                    {player.draft_round || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Draft Round</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">
                    {player.draft_pick || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Draft Pick</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">
                    {player.is_active ? 'Active' : 'Inactive'}
                  </div>
                  <div className="text-xs text-gray-500">Status</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Personal Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-6"
        >
          <button
            onClick={() => toggleSection('personal')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Personal Information</h2>
            </div>
            {expandedSection === 'personal' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'personal' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <div className="text-sm text-gray-500 mb-1">Full Name</div>
                <div className="text-white">{playerName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Birth Date</div>
                <div className="text-white">{birthDate}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Birth Place</div>
                <div className="text-white">
                  {player.person?.birth_city || 'N/A'}
                  {player.person?.birth_state && `, ${player.person.birth_state}`}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">College</div>
                <div className="text-white">{player.person?.college || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Height</div>
                <div className="text-white">
                  {player.person?.height_inches ? 
                    `${Math.floor(player.person.height_inches / 12)}'${player.person.height_inches % 12}"` : 
                    'N/A'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Weight</div>
                <div className="text-white">
                  {player.person?.weight_pounds ? `${player.person.weight_pounds} lbs` : 'N/A'}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Career Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 mb-6"
        >
          <button
            onClick={() => toggleSection('career')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Career Information</h2>
            </div>
            {expandedSection === 'career' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'career' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Draft</div>
                  <div className="text-white">{draftInfo}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Years Pro</div>
                  <div className="text-white">
                    {player.draft_year ? new Date().getFullYear() - player.draft_year : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Position</div>
                  <div className="text-white">{position}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Jersey Number</div>
                  <div className="text-white">#{jerseyNumber}</div>
                </div>
              </div>
              
              {player.salary && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Salary</div>
                  <div className="text-white">${player.salary.toLocaleString()}</div>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Career Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <button
            onClick={() => toggleSection('stats')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Career Statistics</h2>
            </div>
            {expandedSection === 'stats' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSection === 'stats' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {player.career_stats ? (
                <div className="space-y-6">
                  {/* Basic Career Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-primary">
                        {player.career_stats.seasons_played || 0}
                      </div>
                      <div className="text-xs text-gray-400">Seasons</div>
                    </div>
                    <div className="text-center p-4 bg-gray-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-secondary">
                        {player.career_stats.games_played || 0}
                      </div>
                      <div className="text-xs text-gray-400">Games</div>
                    </div>
                    <div className="text-center p-4 bg-gray-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-accent">
                        {player.career_stats.games_started || 0}
                      </div>
                      <div className="text-xs text-gray-400">Games Started</div>
                    </div>
                    <div className="text-center p-4 bg-gray-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-white">
                        {player.career_stats.points || 0}
                      </div>
                      <div className="text-xs text-gray-400">Total Points</div>
                    </div>
                  </div>

                  {/* Sport-Specific Stats */}
                  {/* Basketball Stats */}
                  {(player.career_stats.field_goals_made > 0 || player.career_stats.rebounds > 0) && (
                    <div className="border border-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Basketball Statistics
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-gray-400">Field Goals</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.field_goals_made || 0}/{player.career_stats.field_goals_attempted || 0}
                          </div>
                          <div className="text-xs text-gray-500">
                            {player.career_stats.field_goal_percentage ?
                              `${(player.career_stats.field_goal_percentage * 100).toFixed(1)}%` :
                              '0.0%'}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Rebounds</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.rebounds || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Assists</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.assists || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">3-Pointers</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.three_pointers_made || 0}
                          </div>
                        </div>
                        {player.career_stats.steals > 0 && (
                          <div>
                            <div className="text-sm text-gray-400">Steals</div>
                            <div className="text-lg font-semibold text-white">
                              {player.career_stats.steals}
                            </div>
                          </div>
                        )}
                        {player.career_stats.blocks > 0 && (
                          <div>
                            <div className="text-sm text-gray-400">Blocks</div>
                            <div className="text-lg font-semibold text-white">
                              {player.career_stats.blocks}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Football Stats */}
                  {(player.career_stats.passing_yards > 0 || player.career_stats.rushing_yards > 0) && (
                    <div className="border border-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Football Statistics
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {player.career_stats.passing_yards > 0 && (
                          <>
                            <div>
                              <div className="text-sm text-gray-400">Passing Yards</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.passing_yards.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Passing TDs</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.passing_touchdowns || 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Passer Rating</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.passing_rating || '0.0'}
                              </div>
                            </div>
                          </>
                        )}
                        {player.career_stats.rushing_yards > 0 && (
                          <>
                            <div>
                              <div className="text-sm text-gray-400">Rushing Yards</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.rushing_yards.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Rushing TDs</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.rushing_touchdowns || 0}
                              </div>
                            </div>
                          </>
                        )}
                        {player.career_stats.receiving_yards > 0 && (
                          <>
                            <div>
                              <div className="text-sm text-gray-400">Receiving Yards</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.receiving_yards.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Receptions</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.receptions || 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Receiving TDs</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.receiving_touchdowns || 0}
                              </div>
                            </div>
                          </>
                        )}
                        {player.career_stats.tackles > 0 && (
                          <>
                            <div>
                              <div className="text-sm text-gray-400">Tackles</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.tackles}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-400">Sacks</div>
                              <div className="text-lg font-semibold text-white">
                                {player.career_stats.sacks || 0}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Baseball Stats */}
                  {(player.career_stats.at_bats > 0 || player.career_stats.hits > 0) && (
                    <div className="border border-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Baseball Statistics
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-gray-400">Batting Average</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.batting_average || '0.000'}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Hits</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.hits || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Home Runs</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.home_runs || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">RBIs</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.runs_batted_in || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Runs</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.runs || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">Stolen Bases</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.stolen_bases || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">At Bats</div>
                          <div className="text-lg font-semibold text-white">
                            {player.career_stats.at_bats || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Awards and Achievements */}
                  {(player.career_stats.all_star_selections > 0 ||
                    player.career_stats.championships_won > 0 ||
                    player.career_stats.mvp_awards > 0) && (
                    <div className="border border-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Medal className="w-4 h-4" />
                        Awards & Achievements
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {player.career_stats.all_star_selections > 0 && (
                          <div className="text-center p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                            <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                            <div className="text-lg font-bold text-yellow-400">
                              {player.career_stats.all_star_selections}
                            </div>
                            <div className="text-xs text-gray-400">All-Star Selections</div>
                          </div>
                        )}
                        {player.career_stats.championships_won > 0 && (
                          <div className="text-center p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                            <Trophy className="w-6 h-6 text-green-400 mx-auto mb-2" />
                            <div className="text-lg font-bold text-green-400">
                              {player.career_stats.championships_won}
                            </div>
                            <div className="text-xs text-gray-400">Championships</div>
                          </div>
                        )}
                        {player.career_stats.mvp_awards > 0 && (
                          <div className="text-center p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                            <Award className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                            <div className="text-lg font-bold text-purple-400">
                              {player.career_stats.mvp_awards}
                            </div>
                            <div className="text-xs text-gray-400">MVP Awards</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No career statistics available for this player.</p>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PlayerDetail;